"use client";
import useTranslate from "@app/_hooks/useTranslate";
import {
  ArrowRight as ArrowIcon,
  ChevronRight as ArrowRightIcon,
  UserIcon,
} from "lucide-react";

import { useMarket } from "@app/_context";
import Link from "@components/Link";
import { MarketSelectorButton } from "@components/MarketSelector";
import { Button, Divider } from "@components/ngg";
import { useAuth } from "@context/authContext";
import { useHeaderContext } from "@context/headerContext";
import { useSiteInfo } from "@context/siteInfoContext";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useMenuStore } from "@/stores/menuStore";
import { getOpeningHoursText } from "@/utils/common";

const ContactLinks = () => {
  const { locale, contactNavigation: links } = useHeaderContext();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (!links.length) {
    return null;
  }

  return (
    <div className="space-y-3 pt-4">
      {links.map((link, index) => (
        <p key={`${link?.slug}-${index}`} className="text-base font-normal">
          <Link
            locale={locale}
            href={link?.slug}
            onClick={() => setMobileMenuOpen(false)}
          >
            {link?.title}
          </Link>
        </p>
      ))}
    </div>
  );
};

const CustomerServiceInfo = () => {
  const t = useTranslate();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  const { services, locale } = useHeaderContext();
  const { contentfulLocale } = useMarket().state.market;
  const customerService = services?.at(0);
  if (!customerService) return null;

  const openingHours = getOpeningHoursText({
    contentfulLocale,
    store: customerService,
    t,
  });

  return (
    <div className="pt-4">
      <p className="mb-2 text-sm font-normal uppercase">{t("contactUs")}</p>
      {FEATURE_FLAGS.customerService.phoneLink && (
        <p>
          {t("callUsAt")}
          <a
            className="ml-1 underline"
            href={`tel:${customerService.phoneNumber?.replace(/[-\s]/g, "")}`}
          >
            {customerService.phoneNumber}
          </a>
        </p>
      )}
      {FEATURE_FLAGS.customerService.emailLink && (
        <p>
          {t("emailUsAt")}
          <a
            href={`mailto:${customerService.email}`}
            className="ml-1 underline"
          >
            {customerService?.email}
          </a>
        </p>
      )}

      <p>{openingHours}</p>
      <p>
        <Link
          locale={locale}
          className="underline"
          href={customerService.storePage?.slug}
          onClick={() => setMobileMenuOpen(false)}
        >
          {customerService.storePageLinkText}
        </Link>
        <ArrowIcon strokeWidth={1} className="mb-1 inline size-5 -rotate-45" />
      </p>
    </div>
  );
};

const StoreInfo = () => {
  const t = useTranslate();
  const { locale, stores } = useHeaderContext();
  const { contentfulLocale } = useMarket().state.market;
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (!stores?.length) return null;

  return (
    <div className="pt-4">
      <p className="mb-2 text-sm font-normal uppercase">{t("ourStores")}</p>
      <div className="space-y-4">
        {stores.map((store) => {
          const openingHours = getOpeningHoursText({
            contentfulLocale,
            store,
            t,
          });
          return (
            <div key={store?.sys?.id}>
              <p>{`${store?.title}, ${store?.address}`}</p>
              <p>{openingHours}</p>
              <Link
                locale={locale}
                className="underline"
                href={store.storePage?.slug}
                onClick={() => setMobileMenuOpen(false)}
              >
                {store?.storePageLinkText}
                <ArrowIcon
                  strokeWidth={1}
                  className="mb-1 inline size-5 -rotate-45"
                />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function UserButton() {
  const t = useTranslate();
  const { myPagesStartPage } = useSiteInfo();
  const { locale } = useHeaderContext();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);
  const { auth, showLogin } = useAuth();

  if (auth?.user) {
    return (
      <Button
        as={Link}
        href={myPagesStartPage?.slug || ""}
        locale={locale}
        onClick={() => setMobileMenuOpen(false)}
        className="child:max-w-fit flex w-full items-center gap-1 bg-inherit px-0 py-4 text-black lg:hidden [&>*:last-child]:ml-auto"
        endIcon={<ArrowRightIcon strokeWidth={1} className="size-4.5" />}
        startIcon={<UserIcon strokeWidth={1} className="size-5" />}
      >
        {t("myPages")}
      </Button>
    );
  }

  return (
    <Button
      className="child:max-w-fit flex w-full items-center gap-1 bg-inherit px-0 py-4 text-black lg:hidden [&>*:last-child]:ml-auto"
      onClick={() => {
        showLogin();
        setMobileMenuOpen(false);
      }}
      endIcon={<ArrowRightIcon strokeWidth={1} className="size-4.5" />}
      startIcon={<UserIcon strokeWidth={1} className="size-5" />}
    >
      {t("login")}
    </Button>
  );
}

export default function MobileMenuInfo() {
  return (
    <div className="bg-gray-100 px-4 pb-6 lg:hidden">
      <MarketSelectorButton className="w-full content-start gap-1 py-4" />
      <Divider className="-mx-4 h-px" />
      <UserButton />
      <Divider className="-mx-4 h-px" />
      <ContactLinks />
      <Divider className="-mx-4 mt-4 h-px" />
      <CustomerServiceInfo />
      <StoreInfo />
    </div>
  );
}
